import { Component } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Grid from "@mui/material/Grid";
import Autocomplete from "@mui/material/Autocomplete";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

import { request } from "app/network";

interface AddPointPolicyModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    darkMode?: boolean;
    isEdit?: boolean;
    initialData?: any;
}

interface AddPointPolicyModalState {
    policyName: string;
    policyType: string;
    conditionType: string;
    conditionValue: number;
    pointAmount: number;
    description: string;
    isActive: boolean;
    isDefaultPolicy: boolean;
    priority: number;
    loading: boolean;
    errors: { [key: string]: string };
    policyTypes: Array<{ code: string; name: string }>;
    conditionTypes: Array<{ code: string; name: string }>;
    stepGoalDays: number;
    videoGoalDays: number;
    attendanceGoalDays: number;
    isLimitApplied: boolean;
    isSystemPolicy: boolean;
}

class AddPointPolicyModal extends Component<AddPointPolicyModalProps, AddPointPolicyModalState> {
    constructor(props: AddPointPolicyModalProps) {
        super(props);
        this.state = {
            policyName: "",
            policyType: "",
            conditionType: "",
            conditionValue: 0,
            pointAmount: 0,
            description: "",
            isActive: true,
            isDefaultPolicy: false,
            priority: 0,
            loading: false,
            errors: {},
            policyTypes: [],
            conditionTypes: [],
            stepGoalDays: 0,
            videoGoalDays: 0,
            attendanceGoalDays: 0,
            isLimitApplied: true,
            isSystemPolicy: false,
        };
    }

    async componentDidMount() {
        await this.loadPolicyTypes();
        await this.loadConditionTypes();
    }

    loadPolicyTypes = async () => {
        try {
            const res = await request("CommonCode.getByGroup", {
                paramz: {},
                pathParams: { groupCode: "POINT_POLICY_TYPE" },
            });
            if (res && res.data) {
                const policyTypes = res.data.map((item: any) => ({
                    code: item.code,
                    name: item.name,
                }));
                this.setState({ policyTypes });
            }
        } catch (error) {
            console.error("정책 타입 로드 실패:", error);
        }
    };

    loadConditionTypes = async () => {
        try {
            const res = await request("CommonCode.getByGroup", {
                paramz: {},
                pathParams: { groupCode: "POINT_CONDITION_TYPE" },
            });
            if (res && res.data) {
                const conditionTypes = res.data.map((item: any) => ({
                    code: item.code,
                    name: item.name,
                }));
                this.setState({ conditionTypes });
            }
        } catch (error) {
            console.error("조건 타입 로드 실패:", error);
        }
    };

    handleChange = (field: string) => (event: any) => {
        const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
        console.log(`[handleChange] Field: ${field}, Value: ${value}, Type: ${event.target.type}`);
        this.setState({ [field]: value, errors: { ...this.state.errors, [field]: "" } } as any);
    };

    handleSwitchChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.checked;
        console.log(`[handleSwitchChange] Field: ${field}, Checked: ${value}`);

        // Business Rule: Inactive policies cannot be default policies
        if (field === "isActive" && !value) {
            // If turning off isActive, also turn off isDefaultPolicy
            console.log(`[handleSwitchChange] Deactivating policy, also disabling default policy flag`);
            this.setState({
                isActive: false,
                isDefaultPolicy: false,
                errors: { ...this.state.errors, [field]: "" }
            } as any);
        } else {
            this.setState({ [field]: value, errors: { ...this.state.errors, [field]: "" } } as any);
        }
    };

    validateForm = () => {
        const errors: { [key: string]: string } = {};
        const { policyName, policyType, conditionType, conditionValue, pointAmount } = this.state;

        if (!policyName.trim()) errors.policyName = "정책명을 입력해주세요.";
        if (!policyType.trim()) errors.policyType = "정책 타입을 입력해주세요.";
        if (!conditionType.trim()) errors.conditionType = "조건 타입을 입력해주세요.";
        if ((this.state.policyType !== "MONTHLY_CHALLENGE" && this.state.policyType !== "CHALLENGE_EARN") && conditionValue < 0) errors.conditionValue = "조건 값은 0 이상이어야 합니다.";
        if (conditionType === "VIDEO_COMPLETE") {
            if (pointAmount < 0) errors.pointAmount = "포인트는 0 이상이어야 합니다.";
        } else {
            if (pointAmount <= 0) errors.pointAmount = "포인트는 0보다 커야 합니다.";
        }

        if (policyType === "MONTHLY_CHALLENGE" || policyType === "CHALLENGE_EARN") {
            const { stepGoalDays, videoGoalDays, attendanceGoalDays } = this.state;
            if (stepGoalDays <= 0) errors.stepGoalDays = "걸음수 목표 일수는 0보다 커야 합니다.";
            if (videoGoalDays <= 0) errors.videoGoalDays = "영상 시청 목표 일수는 0보다 커야 합니다.";
            if (attendanceGoalDays <= 0) errors.attendanceGoalDays = "출석 목표 일수는 0보다 커야 합니다.";
        }

        this.setState({ errors });
        return Object.keys(errors).length === 0;
    };

    handleSubmit = async () => {
        if (!this.validateForm()) return;

        this.setState({ loading: true });

        try {
            const param = {
                SeqId: this.props.isEdit ? this.props.initialData?.seqId : undefined,
                PolicyName: this.state.policyName,
                PolicyType: this.state.policyType,
                ConditionType: this.state.conditionType,
                ConditionValue: this.state.conditionValue,
                PointAmount: this.state.pointAmount,
                Description: this.state.description || null,
                IsActive: this.state.isActive,
                Priority: this.state.priority,
                IsDefaultPolicy: this.state.isDefaultPolicy,
                // Note: The backend expects TenantSeqId but Frontend inputs don't allow selecting it explicitly in this modal yet.
                // Assuming it's null (Global) or handled by context.
                StepGoalDays: (this.state.policyType === "MONTHLY_CHALLENGE" || this.state.policyType === "CHALLENGE_EARN") ? this.state.stepGoalDays : null,
                VideoGoalDays: (this.state.policyType === "MONTHLY_CHALLENGE" || this.state.policyType === "CHALLENGE_EARN") ? this.state.videoGoalDays : null,
                AttendanceGoalDays: (this.state.policyType === "MONTHLY_CHALLENGE" || this.state.policyType === "CHALLENGE_EARN") ? this.state.attendanceGoalDays : null,
                IsLimitApplied: this.state.isLimitApplied,
            };

            console.log('[AddPointPolicyModal] Submitting with IsActive:', this.state.isActive, 'Type:', typeof this.state.isActive);
            console.log('[AddPointPolicyModal] Full param:', param);

            const apiName = this.props.isEdit ? "PointPolicy.update" : "PointPolicy.create";
            await request(apiName, { paramz: param });

            this.resetForm();

            this.props.onSuccess();
            this.props.onClose();
        } catch (error: any) {
            console.error(`포인트 정책 ${this.props.isEdit ? "수정" : "생성"} 실패:`, error);
            this.setState({ loading: false });
        }
    };

    resetForm = () => {
        this.setState({
            policyName: "",
            policyType: "",
            conditionType: "",
            conditionValue: 0,
            pointAmount: 0,
            description: "",
            isActive: true,
            isDefaultPolicy: false,
            priority: 0,
            loading: false,
            errors: {},
            stepGoalDays: 0,
            videoGoalDays: 0,
            attendanceGoalDays: 0,
            isLimitApplied: true,
            isSystemPolicy: false,
        });
    }

    componentDidUpdate(prevProps: AddPointPolicyModalProps, prevState: AddPointPolicyModalState) {
        // Only update state when modal transitions from closed to open
        if (!prevProps.open && this.props.open) {
            if (this.props.isEdit && this.props.initialData) {
                const data = this.props.initialData;
                // Only update if the data has actually changed
                const needsUpdate =
                    this.state.policyName !== (data.policyName || "") ||
                    this.state.policyType !== (data.policyType || "") ||
                    this.state.conditionType !== (data.conditionType || "") ||
                    this.state.conditionValue !== (data.conditionValue || 0) ||
                    this.state.pointAmount !== (data.pointAmount || 0) ||
                    this.state.description !== (data.description || "") ||
                    this.state.isActive !== data.isActive ||
                    this.state.isDefaultPolicy !== (data.isDefault || false) ||
                    this.state.priority !== (data.priority || 0) ||
                    this.state.isLimitApplied !== (data.isLimitApplied ?? true) ||
                    this.state.isSystemPolicy !== (data.isSystemPolicy || false);

                if (needsUpdate) {
                    console.log('[AddPointPolicyModal] Loading edit data:', {
                        isActive: data.isActive,
                        isActiveType: typeof data.isActive,
                        finalIsActive: data.isActive !== undefined ? data.isActive : true
                    });

                    this.setState({
                        policyName: data.policyName || "",
                        policyType: data.policyType || "",
                        conditionType: data.conditionType || "",
                        conditionValue: data.conditionValue || 0,
                        pointAmount: data.pointAmount || 0,
                        description: data.description || "",
                        isActive: data.isActive !== undefined ? data.isActive : true,
                        isDefaultPolicy: data.isDefault || false,
                        priority: data.priority || 0,
                        stepGoalDays: data.stepGoalDays || 0,
                        videoGoalDays: data.videoGoalDays || 0,
                        attendanceGoalDays: data.attendanceGoalDays || 0,
                        isLimitApplied: data.isLimitApplied ?? true,
                        isSystemPolicy: data.isSystemPolicy || false,
                        errors: {}
                    });
                }
            } else {
                // Only reset if not already in initial state
                const needsReset =
                    this.state.policyName !== "" ||
                    this.state.policyType !== "" ||
                    this.state.conditionType !== "" ||
                    this.state.conditionValue !== 0 ||
                    this.state.pointAmount !== 0 ||
                    this.state.description !== "" ||
                    !this.state.isActive ||
                    this.state.isDefaultPolicy ||
                    this.state.priority !== 0 ||
                    this.state.stepGoalDays !== 0 ||
                    this.state.videoGoalDays !== 0 ||
                    this.state.attendanceGoalDays !== 0 ||
                    !this.state.isLimitApplied ||
                    this.state.isSystemPolicy;

                if (needsReset) {
                    this.resetForm();
                }
            }
        }
    }

    getFilteredConditionTypes = () => {
        const { policyType, conditionTypes } = this.state;
        if (!policyType) return conditionTypes;

        switch (policyType) {
            case "STEP_EARN":
                return conditionTypes.filter(ct => ct.code === "DAILY_GOAL");
            case "VIDEO_EARN":
                return conditionTypes.filter(ct => ct.code === "DAILY_GOAL" || ct.code === "VIDEO_COMPLETE");
            case "CHALLENGE_EARN":
            case "MONTHLY_CHALLENGE":
                return conditionTypes.filter(ct => ct.code === "MONTHLY_GOAL");
            default:
                return conditionTypes;
        }
    };


    render() {
        const { open, onClose, darkMode } = this.props;
        const { loading, errors } = this.state;

        return (
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: darkMode ? '#1a2035' : '#ffffff',
                        backgroundImage: 'none',
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        color: darkMode ? '#ffffff' : 'inherit',
                        fontWeight: 'medium',
                        fontSize: '1.25rem',
                    }}
                >
                    {this.props.isEdit ? "포인트 정책 수정" : "포인트 정책 추가"}
                </DialogTitle>
                <DialogContent>
                    <MDBox pt={2}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="정책명 *"
                                    value={this.state.policyName}
                                    onChange={this.handleChange("policyName")}
                                    error={!!errors.policyName}
                                    helperText={errors.policyName}
                                    disabled={loading}
                                    sx={{
                                        '& .MuiInputBase-input': {
                                            color: darkMode ? '#ffffff' : 'inherit',
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'inherit',
                                        },
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: darkMode ? 'rgba(255, 255, 255, 0.23)' : 'inherit',
                                            },
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    fullWidth
                                    disablePortal
                                    options={this.state.policyTypes}
                                    getOptionLabel={(option: any) => `${option.name} (${option.code})`}
                                    isOptionEqualToValue={(option: any, value: any) => option.code === value.code}
                                    value={this.state.policyTypes.find(pt => pt.code === this.state.policyType) || null}
                                    onChange={(_, newValue: any) => {
                                        const newPolicyType = newValue?.code || "";
                                        let newConditionType = this.state.conditionType;

                                        // Auto-select MONTHLY_GOAL if MONTHLY_CHALLENGE or CHALLENGE_EARN is selected
                                        if (newPolicyType === "MONTHLY_CHALLENGE" || newPolicyType === "CHALLENGE_EARN") {
                                            newConditionType = "MONTHLY_GOAL";
                                        } else if (newPolicyType === "STEP_EARN") {
                                            newConditionType = "DAILY_GOAL";
                                        }

                                        this.setState({
                                            policyType: newPolicyType,
                                            conditionType: newConditionType,
                                            errors: { ...this.state.errors, policyType: "", conditionType: "" }
                                        });
                                    }}
                                    disabled={loading || this.state.isSystemPolicy}
                                    renderOption={(props, option: any) => {
                                        const { key, ...otherProps } = props as any;
                                        return (
                                            <li key={option.code} {...otherProps}>
                                                {option.name} ({option.code})
                                            </li>
                                        );
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="정책 타입 *"
                                            error={!!errors.policyType}
                                            helperText={errors.policyType}
                                            sx={{
                                                '& .MuiInputBase-input': {
                                                    color: darkMode ? '#ffffff' : 'inherit',
                                                },
                                                '& .MuiInputLabel-root': {
                                                    color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'inherit',
                                                },
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderColor: darkMode ? 'rgba(255, 255, 255, 0.23)' : 'inherit',
                                                    },
                                                },
                                            }}
                                        />
                                    )}
                                    sx={{
                                        '& .MuiAutocomplete-popupIndicator': {
                                            color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'inherit',
                                        },
                                        '& .MuiAutocomplete-clearIndicator': {
                                            color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'inherit',
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    fullWidth
                                    disablePortal
                                    options={this.getFilteredConditionTypes()}
                                    getOptionLabel={(option: any) => `${option.name} (${option.code})`}
                                    isOptionEqualToValue={(option: any, value: any) => option.code === value.code}
                                    value={this.state.conditionTypes.find(ct => ct.code === this.state.conditionType) || null}
                                    onChange={(_, newValue: any) => {
                                        this.setState({
                                            conditionType: newValue?.code || "",
                                            errors: { ...this.state.errors, conditionType: "" }
                                        });
                                    }}
                                    disabled={loading || this.state.isSystemPolicy || !this.state.policyType}
                                    renderOption={(props, option: any) => {
                                        const { key, ...otherProps } = props as any;
                                        return (
                                            <li key={option.code} {...otherProps}>
                                                {option.name} ({option.code})
                                            </li>
                                        );
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="조건 타입 *"
                                            error={!!errors.conditionType}
                                            helperText={!this.state.policyType ? "정책 타입을 먼저 선택해주세요." : errors.conditionType}
                                            disabled={loading || this.state.isSystemPolicy || !this.state.policyType}
                                            sx={{
                                                '& .MuiInputBase-input': {
                                                    color: darkMode ? '#ffffff' : 'inherit',
                                                },
                                                '& .MuiInputLabel-root': {
                                                    color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'inherit',
                                                },
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderColor: darkMode ? 'rgba(255, 255, 255, 0.23)' : 'inherit',
                                                    },
                                                },
                                            }}
                                        />
                                    )}
                                    sx={{
                                        '& .MuiAutocomplete-popupIndicator': {
                                            color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'inherit',
                                        },
                                        '& .MuiAutocomplete-clearIndicator': {
                                            color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'inherit',
                                        },
                                    }}
                                />
                            </Grid>


                            {(this.state.policyType !== "MONTHLY_CHALLENGE" && this.state.policyType !== "CHALLENGE_EARN") && (
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="조건 값 *"
                                        value={this.state.conditionValue}
                                        onChange={this.handleChange("conditionValue")}
                                        error={!!errors.conditionValue}
                                        helperText={errors.conditionValue}
                                        disabled={loading}
                                        sx={{
                                            '& .MuiInputBase-input': {
                                                color: darkMode ? '#ffffff' : 'inherit',
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'inherit',
                                            },
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    borderColor: darkMode ? 'rgba(255, 255, 255, 0.23)' : 'inherit',
                                                },
                                            },
                                            '& input[type=number]': {
                                                colorScheme: darkMode ? 'dark' : 'normal',
                                            },
                                        }}
                                    />
                                </Grid>
                            )}
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="포인트 *"
                                    value={this.state.pointAmount}
                                    onChange={this.handleChange("pointAmount")}
                                    error={!!errors.pointAmount}
                                    helperText={errors.pointAmount}
                                    disabled={loading}
                                    sx={{
                                        '& .MuiInputBase-input': {
                                            color: darkMode ? '#ffffff' : 'inherit',
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'inherit',
                                        },
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: darkMode ? 'rgba(255, 255, 255, 0.23)' : 'inherit',
                                            },
                                        },
                                        '& input[type=number]': {
                                            colorScheme: darkMode ? 'dark' : 'normal',
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="우선순위"
                                    value={this.state.priority}
                                    onChange={this.handleChange("priority")}
                                    disabled={loading}
                                    sx={{
                                        '& .MuiInputBase-input': {
                                            color: darkMode ? '#ffffff' : 'inherit',
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'inherit',
                                        },
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: darkMode ? 'rgba(255, 255, 255, 0.23)' : 'inherit',
                                            },
                                        },
                                        '& input[type=number]': {
                                            colorScheme: darkMode ? 'dark' : 'normal',
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    label="설명"
                                    value={this.state.description}
                                    onChange={this.handleChange("description")}
                                    disabled={loading}
                                    sx={{
                                        '& .MuiInputBase-input': {
                                            color: darkMode ? '#ffffff' : 'inherit',
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'inherit',
                                        },
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: darkMode ? 'rgba(255, 255, 255, 0.23)' : 'inherit',
                                            },
                                        },
                                    }}
                                />
                            </Grid>

                            {/* Monthly Challenge Inputs - Moved to bottom */}
                            {(this.state.policyType === "MONTHLY_CHALLENGE" || this.state.policyType === "CHALLENGE_EARN") && (
                                <>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="걸음수 목표 Day Count *"
                                            value={this.state.stepGoalDays}
                                            onChange={this.handleChange("stepGoalDays")}
                                            error={!!errors.stepGoalDays}
                                            helperText={errors.stepGoalDays}
                                            disabled={loading}
                                            sx={{
                                                '& .MuiInputBase-input': { color: darkMode ? '#ffffff' : 'inherit' },
                                                '& .MuiInputLabel-root': { color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'inherit' },
                                                '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: darkMode ? 'rgba(255, 255, 255, 0.23)' : 'inherit' } },
                                                '& input[type=number]': { colorScheme: darkMode ? 'dark' : 'normal' },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="영상 시청 목표 Day Count *"
                                            value={this.state.videoGoalDays}
                                            onChange={this.handleChange("videoGoalDays")}
                                            error={!!errors.videoGoalDays}
                                            helperText={errors.videoGoalDays}
                                            disabled={loading}
                                            sx={{
                                                '& .MuiInputBase-input': { color: darkMode ? '#ffffff' : 'inherit' },
                                                '& .MuiInputLabel-root': { color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'inherit' },
                                                '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: darkMode ? 'rgba(255, 255, 255, 0.23)' : 'inherit' } },
                                                '& input[type=number]': { colorScheme: darkMode ? 'dark' : 'normal' },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="출석 목표 Day Count *"
                                            value={this.state.attendanceGoalDays}
                                            onChange={this.handleChange("attendanceGoalDays")}
                                            error={!!errors.attendanceGoalDays}
                                            helperText={errors.attendanceGoalDays}
                                            disabled={loading}
                                            sx={{
                                                '& .MuiInputBase-input': { color: darkMode ? '#ffffff' : 'inherit' },
                                                '& .MuiInputLabel-root': { color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'inherit' },
                                                '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: darkMode ? 'rgba(255, 255, 255, 0.23)' : 'inherit' } },
                                                '& input[type=number]': { colorScheme: darkMode ? 'dark' : 'normal' },
                                            }}
                                        />
                                    </Grid>
                                </>
                            )}
                            <Grid item xs={12} sm={4}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={this.state.isLimitApplied}
                                            onChange={this.handleSwitchChange("isLimitApplied")}
                                            disabled={loading || this.state.isSystemPolicy}
                                        />
                                    }
                                    label="포인트 제한 적용"
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={this.state.isActive}
                                            onChange={this.handleSwitchChange("isActive")}
                                            disabled={loading || this.state.isSystemPolicy}
                                        />
                                    }
                                    label="활성화"
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={this.state.isDefaultPolicy}
                                            onChange={this.handleSwitchChange("isDefaultPolicy")}
                                            disabled={loading || !this.state.isActive || this.state.isSystemPolicy}
                                        />
                                    }
                                    label="기본 정책"
                                />
                            </Grid>
                        </Grid>
                    </MDBox>
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={onClose} color="secondary" disabled={loading}>
                        취소
                    </MDButton>
                    <MDButton onClick={this.handleSubmit} color="info" disabled={loading}>
                        {loading ? "저장 중..." : (this.props.isEdit ? "저장" : "추가")}
                    </MDButton>
                </DialogActions>
            </Dialog>
        );
    }
}

export default AddPointPolicyModal;
